// Pure: from prev/next snapshots + new log entries, compute the SFX
// events to fire. Coalescing keeps each step to one attack_thud,
// one *_loses_life per player, one turn-start trumpet (viewer's
// turn only), one boot stomp per size bucket, one spell_cast for
// trap/enchantment plays. Win/lose harp delays past death sfx.

import type { GameView, PlayerId, CardView } from "../api/types";
import type { FeedEntry } from "./feed";
import type { SfxName } from "./sfx";

export interface SfxEvent {
  sound: SfxName;
  /** Milliseconds to wait before firing. Defaults to 0. */
  delayMs?: number;
}

/** Delay between an attack_thud and the *_loses_life that follows
 *  it, so the impact reads as cause-then-consequence rather than a
 *  single muddy chord. */
const HEALTH_AFTER_THUD_MS = 220;

/** Approx longest fatal-life clip — used to back-off the win/lose
 *  harp so it lands after the death thump. Tuned generously; mp3
 *  durations vary. */
const FATAL_HEALTH_DELAY_MS = 1500;

/** Boot-stomp size class for a creature entering battle. Per the spec:
 *    - both atk ≤ 10 AND life ≤ 10 → light
 *    - else if both atk ≤ 20 AND life ≤ 20 → medium
 *    - else (atk > 20 or life > 20) → heavy
 *  Uses the *printed* (base) stats. A buffed 5/5 still stomps light; a
 *  printed 25/3 stomps heavy. */
export function bootStompSize(
  baseAttack: number,
  baseLife: number,
): "light" | "medium" | "heavy" {
  if (baseAttack <= 10 && baseLife <= 10) return "light";
  if (baseAttack <= 20 && baseLife <= 20) return "medium";
  return "heavy";
}

function indexBattle(snap: GameView): Map<number, CardView> {
  const m = new Map<number, CardView>();
  for (const seat of snap.players) {
    for (const c of seat.battle) m.set(c.entity_id, c);
  }
  return m;
}

export function deriveBattleSfx(
  prev: GameView | null,
  next: GameView,
  newEntries: FeedEntry[],
  viewer: PlayerId,
): SfxEvent[] {
  const events: SfxEvent[] = [];

  const prevBattle = prev ? indexBattle(prev) : new Map<number, CardView>();
  const nextBattle = indexBattle(next);

  // Cards entering battle. Trap-set is the same beat as enchantment-cast.
  const sizesSeen = new Set<"light" | "medium" | "heavy">();
  let oppCastableEntered = false;
  for (const [eid, card] of nextBattle) {
    if (prevBattle.has(eid)) continue;
    if (card.type === "creature") {
      sizesSeen.add(bootStompSize(card.base_attack, card.base_life));
    }
    // Cast SFX for OUR plays fires at hover start (BattlePage's
    // submitAction). Skip it here so we don't double-chime when the
    // card lands. For the opponent's plays there's no hover — they
    // get the cast SFX at the moment the card materializes on their
    // side. Applies to every card type now (creatures included), per
    // user request that the cast sound play for ALL plays.
    if (next.players[viewer].battle.every((b) => b.entity_id !== eid)) {
      oppCastableEntered = true;
    }
  }
  if (sizesSeen.has("light")) events.push({ sound: "ai_boot_stomp_light" });
  if (sizesSeen.has("medium")) events.push({ sound: "ai_boot_stomp_medium" });
  if (sizesSeen.has("heavy")) events.push({ sound: "ai_boot_stomp_heavy" });

  // Spell cast log: same hover-vs-post-commit split. Fire here only
  // when the caster is the opponent — our own casts already chimed
  // at the hover beat.
  const oppCastLog = newEntries.some(
    (e) =>
      e.kind === "cast" &&
      e.parts.some((p) => p.type === "player" && p.id !== viewer),
  );
  if (oppCastLog || oppCastableEntered) {
    events.push({ sound: "ai_spell_cast" });
  }

  if (newEntries.some((e) => e.kind === "trap_sprung")) {
    events.push({ sound: "ai_trap_violin_hit" });
  }

  // Damage thud — once per step. (b) catches creatures killed by
  // combat damage that already moved to graveyard before the snapshot.
  let thudFired = false;
  let anyHpLoss = false;
  if (prev) {
    for (const seat of [0, 1] as const) {
      if (next.players[seat].life < prev.players[seat].life) {
        anyHpLoss = true;
        break;
      }
    }
    let creatureCombatHit = false;
    for (const [eid, card] of nextBattle) {
      const was = prevBattle.get(eid)?.damage_marked ?? 0;
      if (card.damage_marked > was) {
        creatureCombatHit = true;
        break;
      }
    }
    if (!creatureCombatHit && prev.combat) {
      const inCombat = new Set<number>();
      for (const a of prev.combat.attackers) inCombat.add(a);
      for (const pair of prev.combat.blocks) {
        inCombat.add(pair.attacker);
        for (const b of pair.blockers) inCombat.add(b);
      }
      for (const eid of inCombat) {
        if (prevBattle.has(eid) && !nextBattle.has(eid)) {
          creatureCombatHit = true;
          break;
        }
      }
    }
    if (creatureCombatHit || anyHpLoss) {
      // Combat-damage steps surface the thud through BattlePage's
      // playCombatLunge, which fires it at the visible lunge apex.
      // Emitting it here too would just double the sound at
      // slightly-different times. For non-combat damage (direct
      // damage spells, mill effects, etc.) we still own the thud
      // because there is no lunge to piggy-back on.
      const inCombatDamage = next.phase === "combat_damage";
      if (!inCombatDamage) {
        events.push({ sound: "ai_attack_thud" });
      }
      thudFired = true;
    }
  }

  // Player Life loss — once per player per step, delayed past the thud
  // so the user reads it as "hit → ouch".
  let fatalThisStep = false;
  const lifeDelay = thudFired ? HEALTH_AFTER_THUD_MS : 0;
  if (prev) {
    for (const seat of [0, 1] as const) {
      const prevLife = prev.players[seat].life;
      const nextLife = next.players[seat].life;
      if (nextLife >= prevLife) continue;
      const isViewer = seat === viewer;
      events.push({
        sound: isViewer ? "ai_player_loses_life" : "ai_opponent_loses_life",
        delayMs: lifeDelay,
      });
      if (nextLife <= 0) fatalThisStep = true;
    }
  }

  // Turn-start trumpet — viewer's turn only, when the playable main
  // phase begins. TurnStart/Draw snapshots can arrive before this.
  if (
    prev &&
    next.phase === "main" &&
    next.active_player === viewer &&
    (prev.phase !== "main" ||
      prev.active_player !== viewer ||
      prev.turn !== next.turn)
  ) {
    events.push({ sound: "ai_turn_start_trumpet" });
  }

  // Result transition (null → set). Delays past the death thump.
  const prevResult = prev?.result ?? null;
  if (!prevResult && next.result) {
    const won =
      next.result.kind === "winner" && next.result.player === viewer;
    events.push({
      sound: won ? "ai_happy_harp_strum" : "ai_harp_strum_sad",
      delayMs: fatalThisStep ? lifeDelay + FATAL_HEALTH_DELAY_MS : 0,
    });
  }

  return events;
}
